/**
 * Globale Schnellsuche (Command-Palette) fuer die Topbar.
 * - Klick aufs Header-Feld ODER ⌘K / Strg+K oeffnet die Palette.
 * - Debounced GET /api/search?q= (tenant-sicher, ILIKE-Teilwortsuche ueber Anfragen,
 *   Kunden, Veranstaltungen, Katalog), Treffer gruppiert, Tastatur-Navigation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { Icon } from "./components";

interface SearchHit {
  type: "inquiry" | "customer" | "event" | "catalog";
  id: string;
  title: string;
  subtitle: string | null;
  meta: string | null;
}
interface SearchResult {
  query: string;
  hits: SearchHit[];
}

const TYPE_META: Record<SearchHit["type"], { label: string; icon: string; href: (id: string) => string }> = {
  inquiry: { label: "Anfragen", icon: "inbox", href: () => "/anfragen" },
  event: { label: "Veranstaltungen", icon: "event", href: (id) => `/veranstaltungen/${id}` },
  customer: { label: "Kunden", icon: "group", href: (id) => `/kunden/${id}` },
  catalog: { label: "Katalog", icon: "menu_book", href: () => "/katalog" },
};
const GROUP_ORDER: SearchHit["type"][] = ["inquiry", "event", "customer", "catalog"];

/** Kleiner Debounce-Hook (kein externer Dep). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term.trim(), 180);
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(0);

  const path = open && debounced.length >= 2 ? `/api/search?q=${encodeURIComponent(debounced)}` : null;
  const { data, loading } = useApi<SearchResult>(path);
  const hits = useMemo(() => data?.hits ?? [], [data]);

  // Beim Oeffnen: Feld leeren + fokussieren.
  useEffect(() => {
    if (open) {
      setTerm("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);
  useEffect(() => setActive(0), [debounced]);

  const go = useCallback(
    (hit: SearchHit) => {
      onClose();
      navigate(TYPE_META[hit.type].href(hit.id));
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && hits[active]) {
      e.preventDefault();
      go(hits[active]!);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  // Gruppieren fuer die Anzeige; flache Reihenfolge fuer Tastatur (active-Index).
  const grouped = GROUP_ORDER.map((type) => ({ type, items: hits.filter((h) => h.type === type) })).filter((g) => g.items.length > 0);
  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[70vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl dark:bg-night-card" role="dialog" aria-label="Suche">
        <div className="flex items-center gap-2 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
          <Icon name="search" className="text-[20px] text-on-surface-variant" />
          <input
            ref={inputRef}
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Anfragen, Kunden, Veranstaltungen, Katalog durchsuchen …"
            className="flex-1 bg-transparent text-[15px] text-on-surface outline-none placeholder:text-on-surface-variant/70 dark:text-inverse-on-surface"
          />
          {loading && <Icon name="progress_activity" className="animate-spin text-[18px] text-on-surface-variant" />}
          <kbd className="hidden items-center rounded-md border border-outline-variant/60 bg-surface-container-lowest px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-on-surface-variant sm:inline-flex dark:border-white/10 dark:bg-white/5">
            Esc
          </kbd>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          {debounced.length < 2 ? (
            <p className="px-4 py-8 text-center font-body-sm text-body-sm text-on-surface-variant/70">
              Mindestens 2 Zeichen eingeben. Navigation mit ↑ ↓, Öffnen mit ⏎.
            </p>
          ) : !loading && hits.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Icon name="search_off" className="text-3xl text-on-surface-variant/60" />
              <p className="font-body-sm text-body-sm text-on-surface-variant">Keine Treffer für „{debounced}“.</p>
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.type} className="mb-1">
                <div className="px-4 py-1 font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">
                  {TYPE_META[g.type].label}
                </div>
                {g.items.map((hit) => {
                  flatIndex++;
                  const idx = flatIndex;
                  return (
                    <button
                      key={`${hit.type}-${hit.id}`}
                      type="button"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(hit)}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
                        active === idx ? "bg-primary/10" : "hover:bg-surface-container-high dark:hover:bg-white/5"
                      }`}
                    >
                      <Icon name={TYPE_META[hit.type].icon} className="shrink-0 text-[18px] text-on-surface-variant" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">{hit.title}</span>
                        {hit.subtitle && <span className="block truncate font-body-sm text-[12px] text-on-surface-variant/80">{hit.subtitle}</span>}
                      </span>
                      {hit.meta && <span className="shrink-0 rounded-md bg-surface-container-high px-1.5 py-0.5 font-label-md text-[10.5px] text-on-surface-variant dark:bg-white/5">{hit.meta}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
